import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { CourseCard } from "@/components/CourseCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, Bot, Search, CheckCircle2, CreditCard, Download, CalendarClock, FileBadge, GraduationCap, FileCheck2, Award, RefreshCcw, Languages, ArrowRight as ArrowR } from "lucide-react";
import { IMAGES } from "@/lib/images";

const steps = [
  { icon: CreditCard, title: "Buy the course", text: "Create an account and purchase the full bundle. Secure payment via Stripe." },
  { icon: Download, title: "Download the material", text: "Get the complete course PDF immediately, aligned with the official certification." },
  { icon: CalendarClock, title: "Schedule the exam", text: "Pick your online examination date from your account." },
  { icon: FileBadge, title: "Earn the certificate", text: "Pass the multiple-choice exam and receive a certificate with a unique verifiable number." },
];

export default function Home() {
  const [courses, setCourses] = useState([]);
  const [code, setCode] = useState("");
  const nav = useNavigate();

  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)).catch(() => {}); }, []);

  return (
    <div className="bg-app">
      <section className="gradient-hero circuit-bg relative overflow-hidden">
        <img src={IMAGES.hero} alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-25 pointer-events-none hidden lg:block" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B132B] via-[#0B132B]/70 to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7 animate-fadeup">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-gold mb-5">LEARN • PROVE • CERTIFY • ADVANCE</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-white">
              Online IT <span className="gold-gradient-text">courses, exams & certifications</span>.
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">
              Prepare for Python, AWS, Azure, and Google Cloud certifications with complete materials,
              timed online examinations, and publicly verifiable certificates.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button onClick={() => nav("/courses")} className="btn-gold h-12 px-7 text-base" data-testid="hero-courses-btn">
                Explore courses
              </Button>
              <Button onClick={() => nav("/verify")} variant="outline" data-testid="hero-verify-btn"
                className="h-12 px-7 text-base border-white/20 text-white hover:bg-white/10 bg-transparent">
                <ShieldCheck className="h-5 w-5 mr-2" /> Verify a certificate
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-300">
              {["8 certification courses", "Free brochures & questions", "Certificates with unique code"].map((t) => (
                <span key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-gold" />{t}</span>
              ))}
            </div>
            <div className="mt-6 flex items-start gap-3 rounded-xl border border-gold/25 bg-gold/5 px-4 py-3 max-w-xl" data-testid="bilingual-note">
              <Languages className="h-5 w-5 text-gold shrink-0 mt-0.5" />
              <p className="text-sm text-slate-200 leading-relaxed">
                All course documentation is in <span className="text-white font-semibold">English</span>. Exams are <span className="text-white font-semibold">bilingual</span> — taken in English plus one of 11 European languages:
                <span className="text-slate-300"> Italian, German, French, Hungarian, Bulgarian, Romanian, Czech, Dutch, Spanish, Polish and Portuguese.</span>
              </p>
            </div>
          </div>

          <div className="lg:col-span-5 animate-fadeup" style={{ animationDelay: "120ms" }}>
            <div className="glass rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="h-5 w-5 text-gold" />
                <h3 className="font-display font-semibold text-white">Quick certificate check</h3>
              </div>
              <p className="text-sm text-slate-400 mb-4">Enter the unique code on the certificate (e.g. CRT-2026-12345-A).</p>
              <div className="flex gap-2">
                <Input value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder="CRT-2026-XXXXX-X" data-testid="hero-verify-input"
                  className="bg-surface-2 border-white/10 text-white font-mono placeholder:text-slate-500" />
                <Button className="btn-gold" data-testid="hero-verify-submit"
                  onClick={() => nav(`/verify${code ? `?code=${encodeURIComponent(code.trim())}` : ""}`)}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                {[["8", "Courses"], ["40h", "Per course"], ["75%", "Pass mark"]].map(([n, l]) => (
                  <div key={l} className="bg-surface-2 rounded-lg py-3 border border-white/5">
                    <div className="font-display text-2xl font-extrabold text-gold">{n}</div>
                    <div className="text-xs text-slate-400 mt-1">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-surface" data-testid="iso-compliance-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
          <div className="flex flex-col items-center text-center">
            <span className="inline-flex items-center gap-2 bg-gold/10 border border-gold/30 text-gold rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide">
              <ShieldCheck className="h-4 w-4" /> ISO/IEC 17024 ALIGNED
            </span>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-white mt-5 max-w-3xl">
              Courses → Examinations → Certifications → Recertifications
            </h2>
            <p className="text-slate-300 mt-4 max-w-3xl leading-relaxed">
              Our end-to-end process is designed in conformity with <span className="text-white font-semibold">ISO/IEC 17024</span> — the
              international standard that sets general requirements for bodies operating the certification of persons.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mt-10">
            {[
              { icon: GraduationCap, t: "Courses", d: "Structured, certification-aligned learning materials." },
              { icon: FileCheck2, t: "Examinations", d: "Impartial, timed, auto-graded online assessment." },
              { icon: Award, t: "Certifications", d: "Unique, publicly verifiable certificate of competence." },
              { icon: RefreshCcw, t: "Recertifications", d: "Renew and keep your credential current over time." },
            ].map((s, i) => (
              <div key={i} className="relative bg-surface-card rounded-xl p-6 border border-white/10 flex flex-col" style={{ backgroundColor: "#131C38" }}>
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-lg bg-electric/15 border border-electric/30 flex items-center justify-center"><s.icon className="h-5 w-5 text-electric" /></div>
                  {i < 3 && <ArrowR className="h-5 w-5 text-gold/50 ml-auto hidden lg:block" />}
                </div>
                <h3 className="font-display font-semibold text-white text-lg mt-4">{s.t}</h3>
                <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-gold mb-2">Catalog</p>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">Certification courses</h2>
          </div>
          <Link to="/courses" className="text-gold font-semibold hover:underline">View all →</Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {courses.slice(0, 8).map((c, i) => <CourseCard key={c.slug} c={c} index={i} />)}
        </div>
      </section>

      <section className="bg-surface border-y border-white/10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-gold mb-2">How it works</p>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">From learning to certification in 4 steps</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-4">
            {steps.map((s, i) => (
              <div key={i} className="relative bg-surface-card rounded-xl p-6 border border-white/10" style={{ backgroundColor: "#131C38" }}>
                <div className="h-12 w-12 rounded-lg bg-gold/15 border border-gold/30 flex items-center justify-center mb-4">
                  <s.icon className="h-6 w-6 text-gold" />
                </div>
                <div className="absolute top-5 right-6 font-display text-4xl font-extrabold text-white/5">{i + 1}</div>
                <h3 className="font-display font-semibold text-white text-lg">{s.title}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20" data-testid="cert-draft-section">
        <div className="text-center mb-10">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-gold mb-2">Your reward</p>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">The certificate you'll earn</h2>
          <p className="text-slate-300 mt-4 max-w-2xl mx-auto leading-relaxed">
            A branded, publicly verifiable certificate with a unique code and QR verification —
            issued the moment you pass your exam.
          </p>
        </div>
        <div className="flex justify-center animate-fadeup">
          <div className="relative rounded-2xl border border-white/10 bg-surface p-2 sm:p-3 shadow-2xl max-w-4xl w-full">
            <span className="absolute top-4 left-4 z-10 bg-gold text-[#071B3D] text-xs font-bold px-3 py-1 rounded-full shadow">SAMPLE</span>
            <img src={`${API}/certificate-preview?format=jpg`} alt="Sample Certalyth Academy certificate"
              className="w-full rounded-lg" data-testid="cert-draft-image" loading="lazy" />
          </div>
        </div>
        <div className="text-center mt-8">
          <Button onClick={() => nav("/courses")} className="btn-gold h-12 px-7 text-base" data-testid="cert-draft-cta">
            <Award className="h-5 w-5 mr-2" /> Start earning yours
          </Button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20">
        <div className="glass rounded-2xl p-8 lg:p-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-electric/15 text-electric rounded-full px-3 py-1 text-xs font-semibold mb-4">
              <Bot className="h-4 w-4" /> AI Assistant
            </div>
            <h2 className="font-display text-3xl font-bold text-white">Ask the Certalyth AI assistant</h2>
            <p className="text-slate-300 mt-4 leading-relaxed">
              An AI assistant that knows the content of every course, the exam conditions, and the platform policies.
              It answers technical questions and guides you through the certification process — in English.
            </p>
            <p className="text-sm text-slate-400 mt-4">Tap the button in the bottom-right corner to start a conversation.</p>
          </div>
          <div className="flex justify-center">
            <img src={IMAGES.aiOrb} alt="AI Assistant" className="h-56 w-56 rounded-full object-cover pulse-glow shadow-2xl" />
          </div>
        </div>
      </section>
    </div>
  );
}
